import { Option } from '@sapphire/result';

import { APP_ROUTES } from '@ps/core-config';
import {
  isSpecificAudienceChannel,
  SpecificAudienceReturn,
  Survey,
  SurveyKind,
  SurveyStatusInfo,
  trafficChannelParentId,
} from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import { SurveyChildKey } from '../../survey-creation-shared-state.model';
import { activeChildNavigationExtras } from './active-child-navigation.helper';
import {
  DASHBOARD_NAVIGATION,
  RouterNavigation,
  specificAudienceExitNavigation,
} from './specific-audience-exit.helper';

/**
 * The id a survey is routed by: a project-backed survey by its project id,
 * not the child survey's own id.
 */
export function routableSurveyId(survey: Survey): number {
  return SurveyKind.isProject(survey)
    ? Option.from(survey.projectId).unwrapOr(survey.id)
    : survey.id;
}

/**
 * Returns the navigation to perform when leaving the survey workflow.
 *
 * A Specific Audience channel is edited from its parent's Traffic Channels
 * drawer, so it returns to the parent's Manage Survey page with that drawer
 * reopened. Any other scheduled or launched survey goes back to its own Manage
 * Survey page, carrying the active child so the same locale/wave stays
 * previewed; anything else (a draft, or no survey yet) to the Dashboard.
 */
export function surveyExitNavigation(
  survey: Nullable<Survey>,
  specificAudienceReturn: SpecificAudienceReturn = 'manage-survey',
  activeChildKey: Nullable<SurveyChildKey> = null,
): RouterNavigation {
  return Option.from(survey).mapOr(DASHBOARD_NAVIGATION, (current) =>
    isSpecificAudienceChannel(current)
      ? specificAudienceExitNavigation(
          trafficChannelParentId(current),
          specificAudienceReturn,
        )
      : ownExitNavigation(current, activeChildKey),
  );
}

function ownExitNavigation(
  survey: Survey,
  activeChildKey: Nullable<SurveyChildKey>,
): RouterNavigation {
  return SurveyStatusInfo.for(survey).isPastDraft()
    ? [
        [APP_ROUTES.manageSurvey(routableSurveyId(survey))],
        activeChildNavigationExtras(survey, activeChildKey),
      ]
    : DASHBOARD_NAVIGATION;
}
